/**
 * The signed-in person: their avatar, name and role, opening a menu with
 * Settings and Sign out.
 *
 * It lives in the menu's pinned foot now, not the top bar (TRBUD-439; design v1
 * (a), kept by Eden in v2: "go with the as drawn"): the person and their role sit
 * next to the menu their role decides. On the rail only the avatar shows; the
 * phone draws the same facts at the head of the More sheet (`AccountSummary`).
 * The top bar keeps the bell and the theme button, so the protected header theme
 * button is untouched.
 */
import { useNavigate } from "react-router-dom";
import { primaryRoleOf, type UserRole } from "@truckbuddy/contract";
import { useAppSession } from "../lib/shell";
import { useSignOut } from "./SignOutFlow";
import { Avatar } from "./Avatar";
import { useContextMenu } from "./ContextMenu";
import { NavIcon } from "./NavIcons";

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  manager: "Fleet manager",
  processor: "Repair coordinator",
  mechanic: "Mechanic",
  viewer: "Viewer",
  requester: "Driver",
};

export function roleLabel(role: UserRole | string): string {
  return ROLE_LABEL[role] ?? role;
}

/**
 * The person's own role, not the previewed one: a preview narrows what they may
 * do, it does not change who they are, so the row reads "Admin · previewing as
 * mechanic" rather than "Mechanic".
 */
function useAccountFacts() {
  const { user, realRoles, previewRole } = useAppSession();
  const own = realRoles.length > 0 ? primaryRoleOf(realRoles) : user.role;
  return { user, role: roleLabel(own), previewRole };
}

/** Name, role and any preview, as the foot row and the More sheet both print them. */
function AccountWho() {
  const { user, role, previewRole } = useAccountFacts();
  return (
    <span className="app-account-who">
      <span className="app-account-name">{user.displayName}</span>
      <span className="app-account-role">
        {role}
        {previewRole ? (
          <>
            {" · "}
            <em>previewing as {previewRole}</em>
          </>
        ) : null}
      </span>
    </span>
  );
}

/** The menu foot's account row. */
export function AccountMenu() {
  const menu = useContextMenu();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { user, role } = useAccountFacts();
  return (
    <>
      <button
        type="button"
        className="app-account"
        aria-label={`${user.displayName}, ${role}. Account menu`}
        aria-haspopup="menu"
        title={`${user.displayName} · ${role}`}
        onClick={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          // Opened at the row's top edge: the row is pinned to the bottom of the
          // screen, and the menu clamps itself into the viewport from there.
          menu.open({ x: box.left, y: box.top }, `${user.displayName} · ${role}`, [
            { key: "settings", label: "Settings", onSelect: () => navigate("/settings") },
            { key: "sign-out", label: "Sign out", onSelect: signOut },
          ]);
        }}
      >
        <Avatar
          name={user.displayName}
          userId={user.userId}
          avatarVersion={user.avatarVersion ?? null}
          title=""
        />
        <AccountWho />
        <NavIcon name="chevron-down" className="app-account-chevron" />
      </button>
      {menu.render()}
    </>
  );
}

/** The More sheet's head: the same facts, not a control (the sheet holds Settings and Sign out itself). */
export function AccountSummary() {
  const { user } = useAccountFacts();
  return (
    <div className="app-sheet-head">
      <Avatar
        name={user.displayName}
        userId={user.userId}
        avatarVersion={user.avatarVersion ?? null}
        title=""
      />
      <AccountWho />
    </div>
  );
}
